import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Link,
} from "@heroui/react";
import { AlertCircle, CreditCard, Upload, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface InsufficientCreditsModalProps {
    isOpen: boolean;
    onClose: () => void;
    message?: string;
    topUpUrl?: string;
}

// Helper function to parse and format the message
function parseMessage(message: string) {
    const lines = message.split('\n').filter(line => line.trim());
    const parsedContent: Array<{ type: 'text' | 'link'; content: string; url?: string; icon?: string }> = [];

    lines.forEach(line => {
        const trimmedLine = line.trim();

        // Check for emoji patterns and extract URLs
        if (trimmedLine.includes('📤') || trimmedLine.toLowerCase().includes('upload')) {
            const urlMatch = trimmedLine.match(/(https?:\/\/[^\s]+)/);
            parsedContent.push({
                type: 'link',
                content: 'Upload past questions',
                url: urlMatch ? urlMatch[1] : '/upload',
                icon: 'upload'
            });
        } else if (trimmedLine.includes('💳') || trimmedLine.toLowerCase().includes('purchase')) {
            const urlMatch = trimmedLine.match(/(https?:\/\/[^\s]+)/);
            parsedContent.push({
                type: 'link',
                content: 'Purchase credits',
                url: urlMatch ? urlMatch[1] : '/credits/top-up',
                icon: 'credit'
            });
        } else if (trimmedLine.includes('⏰') || trimmedLine.toLowerCase().includes('wait') || trimmedLine.toLowerCase().includes('reset')) {
            parsedContent.push({
                type: 'text',
                content: trimmedLine.replace(/⏰|📤|💳/g, '').trim(),
                icon: 'clock'
            });
        } else if (!trimmedLine.match(/https?:\/\//)) {
            // Regular text line (header/intro)
            parsedContent.push({
                type: 'text',
                content: trimmedLine.replace(/📤|💳|⏰/g, '').trim(),
            });
        }
    });

    return parsedContent;
}

export function InsufficientCreditsModal({
    isOpen,
    onClose,
    message,
}: InsufficientCreditsModalProps) {
    const navigate = useNavigate();

    const parsedContent = message ? parseMessage(message) : [];
    const headerText = parsedContent.find(item => item.type === 'text' && !item.icon)?.content || "You're out of credits.";

    const handleLinkClick = (url: string) => {
        try {
            const urlObj = new URL(url);
            navigate(urlObj.pathname);
        } catch {
            navigate(url);
        }
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            placement="center"
            backdrop="blur"
            classNames={
                {
                    base: 'rounded'
                }
            }
        >
            <ModalContent>
                <ModalHeader className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 text-danger">
                        <AlertCircle className="h-6 w-6" />
                        <h2 className="text-xl font-semibold">Insufficient Credits</h2>
                    </div>
                </ModalHeader>
                <ModalBody>
                    <div className="flex flex-col gap-4">
                        {/* Header message */}
                        <p className="text-sm text-default-700">
                            {headerText}
                        </p>

                        {/* Options */}
                        <div className="flex flex-col gap-3">
                            {parsedContent.map((item, index) => {
                                if (item.type === 'link') {
                                    return (
                                        <div key={index} className="flex items-start gap-3 p-3 rounded bg-default-100 hover:bg-default-200 transition-colors cursor-pointer" onClick={() => handleLinkClick(item.url!)}>
                                            <div className="flex-shrink-0 mt-0.5">
                                                {item.icon === 'upload' && <Upload className="h-5 w-5 text-primary" />}
                                                {item.icon === 'credit' && <CreditCard className="h-5 w-5 text-success" />}
                                            </div>
                                            <div className="flex-1">
                                                <Link
                                                    className="text-sm font-medium cursor-pointer"
                                                    onPress={() => handleLinkClick(item.url!)}
                                                >
                                                    {item.content}
                                                </Link>
                                                <p className="text-xs text-default-500 mt-0.5">{item.url}</p>
                                            </div>
                                        </div>
                                    );
                                } else if (item.icon === 'clock') {
                                    return (
                                        <div key={index} className="flex items-start gap-3 p-3 rounded bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800">
                                            <Clock className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                                            <p className="text-sm text-warning-700 dark:text-warning-400">
                                                {item.content}
                                            </p>
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="default"
                        variant="flat"
                        onPress={onClose}
                        className="rounded"
                    >
                        Close
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
